import React, { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Pin, PinOff, Trash2 } from 'lucide-react';
import './DropdownMenu.css';

/**
 * Reusable dropdown menu component that can be triggered by any element.
 * Uses portal for positioning and handles click-outside-to-close.
 */
export default function DropdownMenu({
  isOpen,
  onClose,
  options, // [{ value: 'ja', label: 'Japanese', icon: '🇯🇵', description: '...', leftIcon: <Icon />, pinned: false }]
  value,
  onChange,
  triggerRef, // ref to the trigger element for positioning
  showDescription = false,
  showCheck = true,
  className = '',
  minWidth = 160, // minimum width for menu (useful for icon button triggers)
  maxHeight = null, // optional max height override (e.g., 'calc(100vh - 220px)')
  title = null, // optional title text to show above options
  // Action handlers (optional)
  onPin = null, // (optionValue) => void
  onDelete = null, // (optionValue) => void
}) {
  const [hoveredOption, setHoveredOption] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, width: 0 });
  const menuRef = useRef(null);

  // Calculate menu position based on trigger element
  const updatePosition = () => {
    if (triggerRef?.current && menuRef?.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const menuWidth = Math.max(rect.width, minWidth);
      const menuHeight = menuRef.current.offsetHeight || 200; // Estimate if not rendered yet

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const gap = 6; // Gap between trigger and menu

      // Horizontal positioning
      const wouldOverflowRight = rect.left + menuWidth > viewportWidth - 8; // 8px padding
      const left = wouldOverflowRight
        ? rect.right + window.scrollX - menuWidth
        : rect.left + window.scrollX;

      // Vertical positioning - check if menu would overflow bottom
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const wouldOverflowBottom = spaceBelow < menuHeight + gap + 8; // 8px padding

      // If would overflow bottom AND there's more space above, show above trigger
      const showAbove = wouldOverflowBottom && spaceAbove > spaceBelow;
      const top = showAbove
        ? rect.top + window.scrollY - menuHeight - gap
        : rect.bottom + window.scrollY + gap;

      setMenuPosition({
        top: top,
        left: left,
        width: menuWidth,
      });
    }
  };

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      const clickedInsideMenu = menuRef.current && menuRef.current.contains(e.target);
      const clickedInsideTrigger = triggerRef?.current && triggerRef.current.contains(e.target);

      if (!clickedInsideMenu && !clickedInsideTrigger) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose, triggerRef]);

  // Update position when opening or on scroll/resize
  useEffect(() => {
    if (isOpen) {
      // Initial position calculation
      updatePosition();

      // Recalculate after render (when we have accurate menu height)
      const timer = setTimeout(updatePosition, 0);

      window.addEventListener('scroll', updatePosition, true); // Use capture for all scrolls
      window.addEventListener('resize', updatePosition);
      return () => {
        clearTimeout(timer);
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isOpen]);

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      ref={menuRef}
      className={`dropdown-menu-portal ${className}`}
      style={{
        position: 'absolute',
        top: `${menuPosition.top}px`,
        left: `${menuPosition.left}px`,
        width: `${menuPosition.width}px`,
        zIndex: 9999,
      }}
    >
      {title && <div className="dropdown-menu-title">{title}</div>}
      <ul
        className={`dropdown-menu-list ${showDescription ? 'with-description' : ''}`}
        style={maxHeight ? { maxHeight } : undefined}
      >
        {options.map((option) => {
          const isHovered = hoveredOption === option.value;
          const isPinned = option.pinned;
          const showHoverActions = isHovered && (onPin || onDelete);
          const showPinnedIndicator = !isHovered && isPinned && onPin;

          return (
            <li
              key={option.value}
              onMouseEnter={() => setHoveredOption(option.value)}
              onMouseLeave={() => setHoveredOption(null)}
            >
              <div className="dropdown-option-wrapper">
                <button
                  type="button"
                  className={`dropdown-option ${value === option.value ? 'selected' : ''} ${showDescription ? 'has-description' : ''}`}
                  onClick={() => handleSelect(option.value)}
                >
                  {/* Left icon (e.g., course icon) */}
                  {option.leftIcon && <span className="dropdown-left-icon">{option.leftIcon}</span>}

                  {/* Regular icon (emoji/flag) */}
                  {option.icon && <span className="dropdown-icon">{option.icon}</span>}

                  <div className="dropdown-option-content">
                    <span className="dropdown-option-label">{option.label}</span>
                    {showDescription && option.description && (
                      <span className="dropdown-option-description">{option.description}</span>
                    )}
                  </div>

                  {showCheck && value === option.value && <Check size={18} className="dropdown-check" />}
                </button>

                {/* Pinned indicator - always visible when pinned (not hovered) */}
                {showPinnedIndicator && (
                  <div className="dropdown-option-actions pinned-indicator">
                    <button
                      type="button"
                      className="dropdown-action-btn pin-btn pinned"
                      onClick={(e) => {
                        e.stopPropagation();
                        onPin(option.value);
                      }}
                      title="Pinned"
                    >
                      <Pin size={16} />
                    </button>
                  </div>
                )}

                {/* Action buttons (pin, delete) - show on hover */}
                {showHoverActions && (
                  <div className="dropdown-option-actions">
                    {onPin && (
                      <button
                        type="button"
                        className="dropdown-action-btn pin-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          onPin(option.value);
                        }}
                        title={option.pinned ? 'Unpin' : 'Pin'}
                      >
                        {option.pinned ? <PinOff size={16} /> : <Pin size={16} />}
                      </button>
                    )}
                    {onDelete && (
                      <button
                        type="button"
                        className="dropdown-action-btn delete-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(option.value);
                        }}
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>,
    document.body
  );
}

